import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../stores/authStore';
import { usePermissions } from '../../features/admin/hooks/usePermissions';
import { TenantBranding } from '../../hooks/useTenantBranding';
import { ALL_NAV_ITEMS, NavItemConfig, ClipboardCheckIcon } from './navConfig';
import { LanguageSelector } from './LanguageSelector';
import { useTranslation } from '../../i18n';

export interface ResponsiveNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  branding: TenantBranding | null;
}

export const ResponsiveNavDrawer: React.FC<ResponsiveNavDrawerProps> = ({
  isOpen,
  onClose,
  branding,
}) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isTenantAdmin, isPlatformAdmin, can } = usePermissions();
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close drawer automatically on route changes
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname, location.hash]);

  // Handle escape key and lock body scroll
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const topNavLogo = branding?.topNavLogoUrl;
  const orgName = branding?.name;

  // Filter items based on current user permissions and role
  const isItemPermitted = (item: NavItemConfig): boolean => {
    if (item.platformAdminOnly && !isPlatformAdmin) return false;
    if (item.adminOnly && !isTenantAdmin && !isPlatformAdmin) return false;
    if (item.requiredPermission && !can(item.requiredPermission) && !isTenantAdmin && !isPlatformAdmin) return false;
    return true;
  };

  const permittedItems = ALL_NAV_ITEMS.filter(isItemPermitted);

  const workspaceItems = permittedItems.filter((i) => i.group === 'WORKSPACE');
  const administrationItems = permittedItems.filter((i) => i.group === 'ADMINISTRATION');
  const settingsItems = permittedItems.filter((i) => i.group === 'SETTINGS');

  const isLearner = !isTenantAdmin && !isPlatformAdmin;

  const renderNavGroup = (title: string, items: NavItemConfig[]) => {
    if (items.length === 0) return null;

    const translatedTitle =
      title.toUpperCase() === 'WORKSPACE'
        ? t('nav.group_workspace', undefined, 'Workspace')
        : title.toUpperCase() === 'ADMINISTRATION'
        ? t('nav.group_administration', undefined, 'Administration')
        : title.toUpperCase() === 'SETTINGS'
        ? t('nav.settings', undefined, 'Settings')
        : title;

    const getTranslatedNavLabel = (item: NavItemConfig): string => {
      const idMap: Record<string, string> = {
        admin_console: 'nav.admin_console',
        simulations_learner: 'nav.simulations',
        attempts: 'nav.my_attempts',
        scenarios_studio: 'nav.scenarios',
        learners_admin: 'nav.learners_users',
        reports: 'nav.reports',
        tenants_list: 'nav.organizations',
        evaluations_studio: 'nav.rubrics',
        analytics_deep: 'nav.analytics',
        llm_logs: 'nav.llm_logs',
        audit_logs: 'nav.audit_trail',
        settings: 'nav.settings',
      };
      const key = idMap[item.id] || `nav.${item.id}`;
      return t(key, undefined, item.label);
    };

    return (
      <div className="app-drawer__group">
        <div className="app-drawer__section-title">{translatedTitle}</div>
        <nav className="app-drawer__nav" aria-label={translatedTitle}>
          {items.map((item) => {
            const active = location.pathname === item.path || (item.path !== '/dashboard' && item.path !== '/admin' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            const displayLabel = getTranslatedNavLabel(item);

            return (
              <Link
                key={item.id}
                to={item.path}
                className={`app-drawer__link ${active ? 'active' : ''}`}
                onClick={onClose}
                aria-current={active ? 'page' : undefined}
              >
                <span className="app-drawer__link-icon">
                  <Icon size={18} />
                </span>
                <span className="app-drawer__link-label">{displayLabel}</span>
                {item.badge && <span className="app-drawer__badge">{item.badge}</span>}
              </Link>
            );
          })}
        </nav>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop with click-to-close */}
      <div
        className={`app-drawer-backdrop ${isOpen ? 'active' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-out Drawer */}
      <div
        ref={drawerRef}
        className={`app-drawer ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation Menu"
      >
        {/* Drawer Header */}
        <div className="app-drawer__header">
          <div className="app-drawer__brand">
            {topNavLogo ? (
              <img
                src={topNavLogo}
                alt={orgName || 'Tenant Logo'}
                className="app-drawer__brand-logo"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            ) : (
              <span className="app-drawer__logo-text">
                Trainova<span style={{ color: 'var(--blue)' }}>.ai</span>
              </span>
            )}
            {orgName && (
              <span className="app-drawer__org-badge" title={orgName}>
                {orgName}
              </span>
            )}
          </div>

          <button
            type="button"
            className="app-drawer__close-btn"
            onClick={onClose}
            aria-label={t('nav.close', undefined, 'Close menu')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Drawer Scrollable Body */}
        <div className="app-drawer__body">
          {renderNavGroup('WORKSPACE', workspaceItems)}
          {!isLearner && renderNavGroup('ADMINISTRATION', administrationItems)}
          {!isLearner && renderNavGroup('SETTINGS', settingsItems)}

          {/* Quick Account Links for Learner */}
          {isLearner && (
            <div className="app-drawer__group">
              <div className="app-drawer__section-title">{t('nav.group_account', undefined, 'Account & Support')}</div>
              <nav className="app-drawer__nav" aria-label="Account Links">
                <Link
                  to="/attempts"
                  className={`app-drawer__link ${location.pathname.startsWith('/attempts') ? 'active' : ''}`}
                  onClick={onClose}
                >
                  <span className="app-drawer__link-icon">
                    <ClipboardCheckIcon size={18} />
                  </span>
                  <span className="app-drawer__link-label">{t('nav.my_attempts', undefined, 'Evaluation History')}</span>
                </Link>
              </nav>
            </div>
          )}
        </div>

        {/* Drawer Footer with Language Selector, User Metadata & Sign Out */}
        <div className="app-drawer__footer">
          <LanguageSelector variant="drawer" />

          <div className="app-drawer__user-card">
            <div className="app-drawer__user-avatar">
              {user?.email ? user.email.substring(0, 2).toUpperCase() : 'U'}
            </div>
            <div className="app-drawer__user-meta">
              <span className="app-drawer__user-email" title={user?.email || ''}>
                {user?.email || 'User'}
              </span>
              <span className="app-drawer__user-role">
                {user?.role?.replace(/_/g, ' ') || 'Learner'}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="app-drawer__logout-btn"
            onClick={() => {
              onClose();
              logout();
            }}
            aria-label={t('nav.sign_out', undefined, 'Sign out of Trainova')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>{t('nav.sign_out', undefined, 'Sign out')}</span>
          </button>
        </div>
      </div>
    </>
  );
};
